import { FC, useEffect, useState } from "react"
import { Grid, Breadcrumbs } from '@mui/material'
import CustomTable from "../../components/common/CustomTable/CustomTable"
import { useProductStore } from "../../hooks/useProductStore"
import { emphasize, styled } from '@mui/material/styles'
import Chip from '@mui/material/Chip'
import { moneyCL } from "../../toolbox/helpers/money-helpers"
import { Product } from "../../types/slices/productType"
import HistoryIcon from '@mui/icons-material/History'
import { StockHistoryDrawer } from "./components/StockHistoryDrawer"

interface onList {
    page?: number
    perPage?: number
    exactCode?: string
}

const StyledBreadcrumb = styled(Chip)(({ theme }) => {
    const backgroundColor =
        theme.palette.mode === 'light'
            ? theme.palette.grey[100]
            : theme.palette.grey[800]
    return {
        backgroundColor,
        height: theme.spacing(3),
        color: theme.palette.text.primary,
        fontWeight: theme.typography.fontWeightRegular,
        '&:hover, &:focus': {
            backgroundColor: emphasize(backgroundColor, 0.06),
        },
        '&:active': {
            boxShadow: theme.shadows[1],
            backgroundColor: emphasize(backgroundColor, 0.12),
        },
    }
})

const columns = [
    { type: 'text', field: 'code_system', label: 'Código', align: 'center' },
    { type: 'text', field: 'name', label: 'Nombre', align: 'center' },
    { type: 'text', field: 'description', label: 'Descripcion', align: 'center' },
    { type: 'text', field: 'cost', label: 'Costo', align: 'right', format: (row) => moneyCL(row.cost) },
    { type: 'text', field: 'total_stock', label: 'Stock total', align: 'center' },
    { type: 'detail', field: 'detail', label: 'Detalle', align: 'center' },
]

export const StockProduct: FC = () => {
    const { getProducts, products, totalRows } = useProductStore()

    const [loading, setLoading] = useState<boolean>(false)
    const [searchTimeout, setSearchTimeout] = useState<any>(null)
    const [showDrawer, setShowDrawer] = useState<string>('')
    const [rowSelected, setRowSelected] = useState<Product>({} as Product)

    const [search, setSearch] = useState<string>('')
    const [page, setPage] = useState<number>(1)
    const [perPage, setPerPage] = useState<number>(10)

    useEffect(() => {
        if (search === '') {
            getProductsApi()
        } else {
            const timer = setTimeout(() => getProductsApi(), 500)
            return () => clearTimeout(timer)
        }
    }, [page, perPage, search])

    useEffect(() => {
        if (rowSelected?.id) {
            const product = products?.find(row => row.id === rowSelected?.id)
            setRowSelected(product || {} as Product)
        }
    }, [products, rowSelected])

    const getProductsApi = async () => {
        const payload: onList = {
            page: page,
            perPage: perPage
        }
        if (search !== '') {
            payload.exactCode = search
        }
        await getProducts(payload)
    }

    const onSearch = (term: string) => {
        setSearch(term)
        setPage(1)
    }

    const onHistory = (rowSelected: Product) => {
        setRowSelected(rowSelected)
        setShowDrawer('history')
    }

    const handleChangePage = (event: any, newPage: any) => {
        setPage(newPage + 1)
    }

    const handleChangeRowsPerPage = (event) => {
        setPerPage(+event.target.value)
        setPage(1)
    }

    return (
        <>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mt: 3 }}>
                <StyledBreadcrumb
                    label={"Inventario"}
                />
                <StyledBreadcrumb
                    label={"Inventario físico"}
                    onClick={() => {
                        getProductsApi()
                    }}
                />
            </Breadcrumbs>
            <Grid item xs={12}>
                <CustomTable
                    title={'Inventario físico'}
                    columns={columns}
                    loading={loading}
                    rows={products || []}
                    onRowClick={() => { }}
                    hasOptions
                    onSearch={onSearch}
                    onHelper={onHistory}
                    tooltip_helper="Ver movimientos"
                    icon_helper={<HistoryIcon fontSize="small" />}
                    dense

                    totalRows={totalRows}
                    rowsPerPage={perPage}
                    page={page}
                    handleChangePage={handleChangePage}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                    withCustomPagination
                />
            </Grid>
            {showDrawer === 'history' && (
                <StockHistoryDrawer
                    open={showDrawer === 'history' ? true : false}
                    onClose={() => setShowDrawer('')}
                    product={rowSelected}
                    getProductsApi={getProductsApi}
                />
            )}
        </>
    )
}